'use client'

import { useContext, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useRouter } from 'next/navigation'
import SignUpComponent from '../component/SignUpComponent'
import Modal from '../component/Modal'
import { useTranslation } from 'react-i18next'
import TooltipModal from '../component/TooltipModal'
import { getIdToken } from 'firebase/auth'
import CharacterInputs from './CharacterInputs'
import { ChatCreateContext } from '../contexts/ChatCreateContext'

const CreateCharacter = ({ safe }) => {
  const { user, subscription } = useAuth()
  const [toggleSignUp] = useState(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false)
  const [isLimitModalOpen, setIsLimitModalOpen] = useState(false)
  const [limitTitle] = useState(null)
  const router = useRouter()
  const { t } = useTranslation()
  const [tooltipModalOpen, setTooltipModalOpen] = useState(false)
  const [count] = useState(0)
  const [countExceededModal, setCountExceededModal] = useState(false)

  const {
    model,
    setModel,
    prompt,
    setPrompt,
    imageSrc,
    setImageSrc,
    isLoading,
    setIsLoading,
    errorText,
    setErrorText,
    activeTab,
    setActiveTab,
    charImage,
    setCharImage,
    bio,
    setBio,
    personality,
    setPersonality,
    interests,
    setInterests,
    name,
    setName,
    occupation,
    setOccupation,
    sexuality,
    setSexuality,
    customSexuality,
    setCustomSexuality,
    gender,
    setGender,
    customGender,
    setCustomGender,
    ethnicity,
    setEthnicity,
    customEthnicity,
    setCustomEthnicity,
    age,
    setAge,
    emotions,
    setEmotions,
    fashion,
    setFashion,
    customFashion,
    setCustomFashion,
    bodyType,
    setBodyType,
    customBodyType,
    setCustomBodyType,
    breastSize,
    setBreastSize,
    locale,
    setLocale,
    faceTab,
    setFaceTab,
    continueOptions,
    setContinueOptions,
    resetStates,
    scenario,
    setScenario,
    openness,
    setOpenness,
    showAttributes,
    setShowAttributes,
    weight,
    setWeight,
    breasts,
    setBreasts,
    ass,
    setAss,
    accent,
    setAccent,
    voice,
    setVoice,
    chatId,
    chatImageModel,
    firstMessage,
    setFirstMessage,
  } = useContext(ChatCreateContext)

  const handleSubmitPost = async (publishType) => {
    if (!user) {
      router.push('/signup')
    }
    try {
      setIsLoading(true)
      const idToken = await getIdToken(user)

      const requestBody = {
        chatId,
        // The source image's model; the backend validates it and prefers the image's own doc.
        model: chatImageModel || undefined,
        imageSrc: [charImage],
        charImage,
        bio,
        personality,
        name,
        occupation,
        sexuality: customSexuality || sexuality,
        gender: customGender || gender,
        ethnicity: customEthnicity || ethnicity,
        age,
        emotions,
        openness,
        fashion: customFashion || fashion,
        bodyType: customBodyType || bodyType,
        breastSize,
        location: locale,
        interests,
        scenario,
        publishType,
        weight_slider: showAttributes ? weight : null,
        breast_slider: showAttributes ? breasts : null,
        ass_slider: showAttributes ? ass : null,
        voice: voice ? voice : null,
        accent: accent ? accent : null,
        first_message: firstMessage ? firstMessage : null,
      }

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_PROMPTCHAN_CLOUD}/api/create_character`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: idToken,
          },
          body: JSON.stringify(requestBody),
        },
      )

      if (response.ok) {
        resetStates()
        const data = await response.json()
        const id = data.id
        if (id) {
          localStorage.setItem('characterId', id)
        }
        if (typeof window !== 'undefined') window.location.reload()
      } else {
        if (response.status === 300) {
          setErrorText(<div className="text-center">Banned word detected.</div>)
          setIsErrorModalOpen(true)
        } else if (response.status === 401) {
          setErrorText(
            <div className="text-center">
              We couldn’t verify that this image meets our Community Guidelines.
              Try a different AI character try again.
            </div>,
          )
          setIsErrorModalOpen(true)
        } else {
          setErrorText(
            <div className="text-center">
              Something went wrong: {response.message}
            </div>,
          )
          setIsErrorModalOpen(true)
        }
        throw new Error('Response not ok')
      }
    } catch (error) {
      console.error('Something went wrong.', error)
    } finally {
      setIsLoading(false)
    }
  }

  const modal = (
    <div
      className="absolute z-50 md:left-1/4 md:right-1/4"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <SignUpComponent message={t('signUpToCreate')} />
    </div>
  )

  return (
    <div className="bg-[var(--middle-bg-color)] ">
      {toggleSignUp && modal}

      <div
        className={`mx-auto px-3 py-3 ${
          !user && toggleSignUp
            ? 'blur-[2px] pointer-events-none opacity-40'
            : ''
        }`}
      >
        <section className="w-full ">
          <div className=" space-y-6 ">
            <div className="w-full ">
              <CharacterInputs
                model={model}
                setModel={setModel}
                prompt={prompt}
                setPrompt={setPrompt}
                handleSubmitPost={handleSubmitPost}
                subscription={subscription?.plan}
                isLoading={isLoading}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                imageSrc={imageSrc}
                setImageSrc={setImageSrc}
                setIsLimitModalOpen={setIsLimitModalOpen}
                setErrorText={setErrorText}
                charImage={charImage}
                setCharImage={setCharImage}
                bio={bio}
                setBio={setBio}
                personality={personality}
                setPersonality={setPersonality}
                interests={interests}
                setInterests={setInterests}
                name={name}
                setName={setName}
                occupation={occupation}
                setOccupation={setOccupation}
                sexuality={sexuality}
                setSexuality={setSexuality}
                setCustomSexuality={setCustomSexuality}
                gender={gender}
                setGender={setGender}
                customGender={customGender}
                setCustomGender={setCustomGender}
                ethnicity={ethnicity}
                setEthnicity={setEthnicity}
                customEthnicity={customEthnicity}
                setCustomEthnicity={setCustomEthnicity}
                age={age}
                setAge={setAge}
                emotions={emotions}
                setEmotions={setEmotions}
                fashion={fashion}
                setFashion={setFashion}
                customFashion={customFashion}
                setCustomFashion={setCustomFashion}
                bodyType={bodyType}
                setBodyType={setBodyType}
                customBodyType={customBodyType}
                setCustomBodyType={setCustomBodyType}
                breastSize={breastSize}
                setBreastSize={setBreastSize}
                locale={locale}
                setLocale={setLocale}
                faceTab={faceTab}
                setFaceTab={setFaceTab}
                continueOptions={continueOptions}
                setContinueOptions={setContinueOptions}
                resetStates={resetStates}
                scenario={scenario}
                setScenario={setScenario}
                count={count}
                setCountExceededModal={setCountExceededModal}
                openness={openness}
                setOpenness={setOpenness}
                showAttributes={showAttributes}
                setShowAttributes={setShowAttributes}
                weight={weight}
                setWeight={setWeight}
                breasts={breasts}
                setBreasts={setBreasts}
                ass={ass}
                setAss={setAss}
                safe={safe}
                accent={accent}
                setAccent={setAccent}
                voice={voice}
                setVoice={setVoice}
                firstMessage={firstMessage}
                setFirstMessage={setFirstMessage}
              />
            </div>

            <div className="flex flex-col lg:w-2/3">
              <div className="w-full rounded-md relative flex items-center justify-center  mt-6">
                <Modal
                  isOpen={countExceededModal}
                  closeModal={() => setCountExceededModal(false)}
                  title="Character Limit Exceeded"
                  content={
                    'You have reached the maximum custom characters for the Free plan. Upgrade to enjoy unlimited custom character creations!'
                  }
                  confirmation={t('upgradeandunlockbenefits')}
                  onConfirm={() => {
                    router.push('/upgrade')
                  }}
                />

                <Modal
                  isOpen={isErrorModalOpen}
                  closeModal={() => setIsErrorModalOpen(false)}
                  title="Error"
                  content={errorText || 'Something went wrong.'}
                  confirmation="Done"
                  onConfirm={() => setIsErrorModalOpen(false)}
                />

                <Modal
                  isOpen={isLimitModalOpen}
                  closeModal={() => setIsLimitModalOpen(false)}
                  title={limitTitle || `🚀 ${t('upgradetocontinuegenerating')}`}
                  content={errorText || 'Something went wrong.'}
                  confirmation={t('upgradeandunlockbenefits')}
                  onConfirm={() => {
                    subscription?.plan === 'Studio'
                      ? router.push('/gems')
                      : router.push('/upgrade')
                  }}
                />

                <TooltipModal
                  isOpen={tooltipModalOpen}
                  closeModal={() => setTooltipModalOpen(false)}
                  title="Welcome to Create"
                  content="With the create tool, you can create images using AI."
                  confirmation="Start Creating"
                  onConfirm={() => setTooltipModalOpen(false)}
                />
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default CreateCharacter
